import { ChevronDown, Plus, Trash2 } from "lucide-react";
import React from "react";

import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  FormLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
} from "@/components";
import { useI18n } from "@/context/i18n";

import type {
  TProductJourneyField,
  TProductJourneyStep,
} from "../journey-config.types";
import { isPresetFieldKey } from "../journey-field-presets";
import { productJourneyFieldWidgetSchema } from "../schemas/journey-config.schema";

const widgetsWithOptions = new Set(["select", "combobox"]);

export interface JourneyFieldEditorProps {
  field: TProductJourneyField;
  sortedSteps: TProductJourneyStep[];
  onUpdate: (patch: Partial<TProductJourneyField>) => void;
  onRenameKey: (newKey: string) => void;
  onMoveToStep: (stepId: string) => void;
  onRemove: () => void;
}

export const JourneyFieldEditor: React.FC<JourneyFieldEditorProps> = ({
  field: f,
  sortedSteps,
  onUpdate,
  onRenameKey,
  onMoveToStep,
  onRemove,
}) => {
  const { t } = useI18n();
  const widgetOptions = productJourneyFieldWidgetSchema.options;
  const keyEditable = !isPresetFieldKey(f.key);
  const showOptions = widgetsWithOptions.has(f.widget);
  const defaultValueStr =
    f.defaultValue === undefined || f.defaultValue === null
      ? ""
      : String(f.defaultValue);

  const [keyDraft, setKeyDraft] = React.useState(f.key);
  React.useEffect(() => {
    setKeyDraft(f.key);
  }, [f.key]);

  return (
    <Collapsible defaultOpen className="border rounded-md">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12 items-end p-3">
        <div className="md:col-span-2">
          <FormLabel className="text-xs text-muted-foreground">
            {t("products.journey.fieldKey")}
          </FormLabel>
          <Input
            value={keyEditable ? keyDraft : f.key}
            readOnly={!keyEditable}
            className="font-mono text-xs"
            onChange={(e) => {
              if (keyEditable) setKeyDraft(e.target.value);
            }}
            onBlur={() => {
              if (keyEditable) onRenameKey(keyDraft);
            }}
          />
        </div>
        <div className="md:col-span-3">
          <FormLabel className="text-xs">{t("products.journey.fieldLabel")}</FormLabel>
          <Input
            value={f.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <FormLabel className="text-xs">{t("products.journey.fieldWidget")}</FormLabel>
          <Select
            value={f.widget}
            onValueChange={(v) =>
              onUpdate({ widget: v as TProductJourneyField["widget"] })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {widgetOptions.map((w) => (
                <SelectItem key={w} value={w}>
                  {t(`products.journey.widget.${w}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <FormLabel className="text-xs">{t("products.journey.fieldStep")}</FormLabel>
          <Select
            value={
              sortedSteps.find((s) => s.fieldKeys.includes(f.key))?.id ?? ""
            }
            onValueChange={onMoveToStep}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("products.journey.pickStep")} />
            </SelectTrigger>
            <SelectContent>
              {sortedSteps.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {(s.title || "").trim() ||
                    `${t("products.journey.step")} ${s.order + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 flex flex-wrap items-center gap-3 pb-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={Boolean(f.required)}
              onCheckedChange={(c) => onUpdate({ required: c })}
            />
            <span className="text-xs">{t("products.journey.fieldRequired")}</span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={f.fillable !== false}
              onCheckedChange={(c) => onUpdate({ fillable: c })}
            />
            <span className="text-xs">{t("products.journey.fieldFillable")}</span>
          </div>
        </div>
        <div className="md:col-span-1 flex justify-end gap-1">
          <CollapsibleTrigger asChild>
            <Button type="button" variant="ghost" size="icon">
              <ChevronDown className="h-4 w-4" />
            </Button>
          </CollapsibleTrigger>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <CollapsibleContent className="px-3 pb-3 space-y-3 border-t bg-muted/30">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 pt-3">
          <div>
            <FormLabel className="text-xs">
              {t("products.journey.fieldPlaceholder")}
            </FormLabel>
            <Input
              value={f.placeholder ?? ""}
              onChange={(e) =>
                onUpdate({ placeholder: e.target.value || undefined })
              }
              placeholder={t("products.journey.fieldPlaceholderHint")}
            />
          </div>
          <div>
            <FormLabel className="text-xs">
              {t("products.journey.fieldDefaultValue")}
            </FormLabel>
            <Input
              value={defaultValueStr}
              onChange={(e) =>
                onUpdate({ defaultValue: e.target.value || undefined })
              }
            />
          </div>
          <div className="md:col-span-2">
            <FormLabel className="text-xs">
              {t("products.journey.fieldHelpText")}
            </FormLabel>
            <Textarea
              rows={2}
              value={f.helpText ?? ""}
              onChange={(e) =>
                onUpdate({ helpText: e.target.value || undefined })
              }
            />
          </div>
          <div>
            <FormLabel className="text-xs">
              {t("products.journey.fieldIntegrationType")}
            </FormLabel>
            <Input
              value={f.integrationType ?? ""}
              readOnly={isPresetFieldKey(f.key)}
              className="font-mono text-xs"
              onChange={(e) =>
                onUpdate({ integrationType: e.target.value || undefined })
              }
              placeholder={t("products.journey.fieldIntegrationTypeHint")}
            />
          </div>
        </div>

        {showOptions && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <FormLabel className="text-xs">
                {t("products.journey.fieldOptions")}
              </FormLabel>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  onUpdate({
                    options: [...(f.options ?? []), { value: "", label: "" }],
                  })
                }
              >
                <Plus className="h-3 w-3 mr-1" />
                {t("products.journey.addOption")}
              </Button>
            </div>
            {(f.options ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("products.journey.noOptionsYet")}
              </p>
            ) : (
              <div className="space-y-2">
                {(f.options ?? []).map((opt, idx) => (
                  <div key={idx} className="flex flex-wrap gap-2 items-end">
                    <div className="flex-1 min-w-[8rem]">
                      <FormLabel className="text-xs">
                        {t("products.journey.optionValue")}
                      </FormLabel>
                      <Input
                        value={opt.value}
                        className="font-mono text-xs"
                        onChange={(e) => {
                          const options = [...(f.options ?? [])];
                          options[idx] = {
                            ...options[idx],
                            value: e.target.value,
                          };
                          onUpdate({ options });
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-[8rem]">
                      <FormLabel className="text-xs">
                        {t("products.journey.optionLabel")}
                      </FormLabel>
                      <Input
                        value={opt.label}
                        onChange={(e) => {
                          const options = [...(f.options ?? [])];
                          options[idx] = {
                            ...options[idx],
                            label: e.target.value,
                          };
                          onUpdate({ options });
                        }}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive shrink-0"
                      onClick={() => {
                        const options = (f.options ?? []).filter(
                          (_, i) => i !== idx,
                        );
                        onUpdate({
                          options: options.length > 0 ? options : undefined,
                        });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
